import { NgForOf, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import * as _ from 'lodash';
import { filter, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { App } from '../../app';
import { EProductTypes } from '../../enums/EProductTypes';
import { IQueryFormat } from '../../enums/types';
import { ICategories, ICategories_P, ICategories_S } from '../../interfaces/ICategories';
import { IMainCategories } from '../../interfaces/IMainCategories';
import { IProducts, IProducts_P, IProducts_S } from '../../interfaces/IProducts';
import { IReviews_P, IReviews_S } from '../../interfaces/IReviews';
import { ISubCategories, ISubCategories_P, ISubCategories_S } from '../../interfaces/ISubCategories';
import { CategoriesService } from '../../service/categories.service';
import { MainCategoriesService } from '../../service/main-categories.service';
import { ProductsService } from '../../service/products.service';
import { ReviewsService } from '../../service/reviews.service';
import { SubCategoriesService } from '../../service/sub-categories.service';
import { SelectedBrandModelService } from '../../service/selected-brand-model.service';
import { Select } from 'primeng/select';
import { Tooltip } from "primeng/tooltip";

@Component({
    selector: 'app-phone-cases',
    standalone: true,
    templateUrl: './phone-cases.html',
    imports: [FormsModule, NgForOf, NgIf, Select, Tooltip, RouterLink],
    styleUrl: 'phone-cases.scss'
})
export class PhoneCases implements OnInit, OnDestroy {
    loading: boolean = false;
    mainCategoryId: string = '';
    mainCategory: IMainCategories = <any>{};
    subCategoryId: string = '';
    subCategories: ISubCategories[] = [];
    categoryIdQueryParam: string = '';
    brandId: string = '';
    modelId: string = '';
    modelName: string = '';
    categories: ICategories[] = [];
    products: IProducts[] = [];
    selectedCategory: ICategories = <any>{};
    fileUrl = environment.FILE_URL;
    topFileUrl: string = '';

    @HostListener('window:scroll', [])
    onWindowScroll() {
        this.app.isScrolled = window.scrollY < 99999;
        this.app.hideTopStrip = window.scrollY < 99999;
    }

    private destroy$ = new Subject<void>();

    EProductTypes = EProductTypes;

    constructor(private app: App,
                private mainCategoriesService: MainCategoriesService,
                private subCategoriesService: SubCategoriesService,
                private categoriesService: CategoriesService,
                private productsService: ProductsService,
                private reviewsService: ReviewsService,
                private router: Router,
                private cd: ChangeDetectorRef,
                private route: ActivatedRoute,
                private selectedBrandModelService: SelectedBrandModelService) {
    }

    async ngOnInit() {
        this.mainCategoryId = <any>this.route.snapshot.queryParamMap.get('mainCategoryId');
        this.subCategoryId = <any>this.route.snapshot.queryParamMap.get('subCategoryId');
        this.categoryIdQueryParam = <any>this.route.snapshot.queryParamMap.get('categoryName');
        this.resolveBrandModel();

        await this.getMainCategory();

        this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.destroy$)).subscribe(() => {
            this.mainCategoryId = <any>this.route.snapshot.queryParamMap.get('mainCategoryId');
            this.subCategoryId = <any>this.route.snapshot.queryParamMap.get('subCategoryId');
            this.categoryIdQueryParam = <any>this.route.snapshot.queryParamMap.get('categoryName');
            this.resolveBrandModel();
            this.getMainCategory().then();
            this.cd.detectChanges();
        });


        this.app.isScrolled = true;
        this.app.hideTopStrip = true;

        const items = document.querySelectorAll(".list-group-item");
        items.forEach(item => {
            item.addEventListener("click", () => {
                items.forEach(i => i.classList.remove("active-category"));
                item.classList.add("active-category");
            });
        });

    }

    // Explicit brandId/modelId query params (deep link, search, category tile) always win for
    // this visit and become the new persisted preference; otherwise fall back to whatever the
    // user picked previously so the filter survives navigating away and back.
    private resolveBrandModel() {
        const brandIdParam = this.route.snapshot.queryParamMap.get('brandId');
        const modelIdParam = this.route.snapshot.queryParamMap.get('modelId');

        if (brandIdParam || modelIdParam) {
            this.brandId = <any>brandIdParam;
            this.modelId = <any>modelIdParam;
            this.modelName = <any>this.route.snapshot.queryParamMap.get('modelName');
            if (brandIdParam && modelIdParam) {
                this.selectedBrandModelService.set({ brandId: this.brandId, modelId: this.modelId, modelName: this.modelName });
            }
            return;
        }

        const stored = this.selectedBrandModelService.get();
        this.brandId = stored?.brandId || '';
        this.modelId = stored?.modelId || '';
        this.modelName = stored?.modelName || '';
    }

    openDetailsPage(product: IProducts) {
        this.router.navigate(['/phone-case-details'], {
            queryParams: {
                product: product._id
            }
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    async getMainCategory() {
        if (this.mainCategoryId) {
            this.mainCategory = <any>await this.mainCategoriesService.getById(this.mainCategoryId);
            await this.loadSubCategory(this.mainCategoryId);
        }
    }

    async loadSubCategory(mainCategoriesIds: string) {
        this.loading = true;
        this.subCategories = [];
        let subCategoryId = <any>this.route.snapshot.queryParamMap.get('subCategoryId');
        if (!subCategoryId) this.subCategoryId = '';
        let query = <IQueryFormat>{
            find: <ISubCategories_P>{
                active: true,
                ssc_main_category_id: <any>{ $in: [mainCategoriesIds] }
            },
            select: <ISubCategories_S>{
                ssc_img_url__arr: 1,
                ssc_name_str: 1,
                ssc_order_num: 1,
                ssc_main_category_id: 1,
            },
            sort: <ISubCategories_S>{ ssc_order_num: 1 }
        };
        this.subCategories = await this.subCategoriesService.query(query);
        this.subCategories.unshift(<any>{ _id: "ALL", ssc_name_str: 'ALL' });
        if (!this.subCategoryId) this.subCategoryId = <any>this.subCategories[0]?._id;
        await this.loadCategories();
        this.loading = false;
        this.cd.detectChanges();
    }

    async loadCategories() {
        this.loading = true;
        this.categories = [];
        this.products = [];
        this.selectedCategory = <any>{};
        if (!this.mainCategoryId) return;

        let findObj = <ICategories_P>{
            active: true,
            cat_main_category_id: <any>this.mainCategoryId,
        };

        if (this.subCategoryId) findObj.cat_sub_category_id = <any>this.subCategoryId;
        if (this.subCategoryId === 'ALL') delete findObj.cat_sub_category_id;
        let query = <IQueryFormat>{
            find: findObj,
            select: <ICategories_S>{
                cat_order_num: 1,
                cat_sub_category_id: 1,
                cat_main_category_id: 1,
                cat_img_url__arr: 1,
                cat_name_str: 1
            },
            sort: <ICategories_S>{ cat_order_num: 1 }
        };
        let resp = <any>await this.categoriesService.query(query);
        if (resp && resp.length) {
            let products = await this.loadProductsCountData(resp);
            let productGroupBy = _.groupBy(products, 'pro_category_id');
            for (let item of resp) {
                if (productGroupBy[item._id]) item.count = productGroupBy[item._id]?.length;
                else item.count = 0;
            }
        }
        this.categories = resp;
        if (this.categories && this.categories.length) await this.loadProducts(this.categories[0], false);
        else this.topFileUrl = 'assets/website-images/homeBanner.png';
        this.loading = false;
        this.cd.detectChanges();
    }

    async loadProductsCountData(categories: ICategories[]) {
        let query = <IQueryFormat>{
            find: <IProducts_P>{
                active: true,
                pro_category_id: <any>{ $in: _.map(categories, '_id') }
            },
            select: <IProducts_S>{
                pro_name_str: 1,
                pro_category_id: 1
            },
            sort: <IProducts_S>{ pro_name_str: 1 }
        };
        if (this.mainCategoryId) query.find.pro_main_category_id = this.mainCategoryId;
        if (this.subCategoryId !== 'ALL' && this.subCategoryId) query.find.pro_sub_category_id = this.subCategoryId;
        if (this.brandId) query.find.pro_brand_id = this.brandId;
        if (this.brandId && this.modelId) query.find.pro_model_id = this.modelId;
        return await this.productsService.query(query);
    }

    async loadProducts(category: ICategories, isSelectedCategory: boolean) {
        this.loading = true;
        if (category) this.selectedCategory = category;
        if (this.subCategoryId === 'ALL') this.selectedCategory = <any>{};
        this.products = [];
        let query = <IQueryFormat>{
            find: <IProducts_P>{
                active: true,
            },
            select: <IProducts_S>{
                pro_name_str: 1,
                createdAt: 1,
                pro_discount_price_num: 1,
                pro_price_num: 1,
                pro_sale_num: 1,
                pro_images_arr: 1,
                pro_percentage_off_price_num: 1,
                pro_product_type_str: 1,
            },
            sort: <IProducts_S>{ pro_name_str: 1 }
        };

        if (this.brandId && this.modelId) {
            query.find.pro_model_id = this.modelId;
        }

        if (this.brandId) query.find.pro_brand_id = this.brandId;

        if (this.subCategoryId !== 'ALL' && this.subCategoryId) {
            query.find.pro_sub_category_id = this.subCategoryId;
            query.find.pro_category_id = category._id;
            this.selectedCategory = category;
        }


        // open the category named in the query param on page load, when redirected from a home
        // page card. A name that matches nothing must be ignored rather than applied — writing
        // an undefined id here used to wipe the sub-category filter resolved just above and
        // dump the shopper on the unfiltered catalog. Only on load: the param stays in the URL
        // after a sidebar click, and must not override the category the shopper just picked.
        if (this.categoryIdQueryParam && !isSelectedCategory) {
            let CaseCategory: ICategories_P | undefined = this.categories.find((catItem) => catItem.cat_name_str == this.categoryIdQueryParam);
            if (CaseCategory) {
                query.find.pro_category_id = CaseCategory._id;
                this.selectedCategory = <any>CaseCategory;
            }
        }

        if (category && category._id && isSelectedCategory) {
            query.find.pro_category_id = category._id;
            this.selectedCategory = category;
        }
        if (this.subCategoryId === 'ALL' && !isSelectedCategory) {
            query.find.pro_category_id = <any>{ $in: _.map(this.categories, '_id') };
            this.selectedCategory = <any>{};
        }

        // banner follows whichever category ended up selected, using its admin-uploaded banner image
        this.topFileUrl = this.getBannerUrl(this.selectedCategory);

        if (this.mainCategoryId) query.find.pro_main_category_id = this.mainCategoryId;
        let resp = await this.productsService.query(query);

        if (resp && resp.length) {
            let productReviews = await this.loadProductsReview(_.map(resp, "_id"));
            let groupByProductReviews = _.groupBy(productReviews, 'swr_product_id');
            for (let item of resp) {
                if (item.pro_images_arr && item.pro_images_arr.length) {
                    const colorArr = item.pro_images_arr;
                    const uploadPath = colorArr[0]?.uploadPath;
                    if (uploadPath) item.fileUrl = `${this.fileUrl}${uploadPath}`;

                    const uploadPath1 = colorArr[1]?.uploadPath;
                    if (uploadPath1) item.fileUrl1 = `${this.fileUrl}${uploadPath1}`;
                }
                let ratingSum = _.sumBy(groupByProductReviews[item._id], (item: any) => Number(item.swr_rating_str));
                let ratingCount = Math.floor(ratingSum / groupByProductReviews[item._id]?.length);
                item.reviewsRatings = Array.from({ length: ratingCount }, (_, i) => i + 1);
            }
        }
        this.products = resp;
        this.loading = false;
        this.cd.detectChanges();
    }

    private getBannerUrl(category: ICategories | ICategories_P): string {
        const uploadPath = category?.cat_img_url__arr?.[0]?.uploadPath;
        return uploadPath ? `${this.fileUrl}${uploadPath}` : 'assets/website-images/homeBanner.png';
    }

    async loadProductsReview(productId: string[]) {
        this.loading = true;
        let query = <IQueryFormat>{
            find: <IReviews_P>{
                active: true,
                swr_product_id: <any>{ $in: productId }
            },
            select: <IReviews_S>{
                swr_product_id: 1,
                swr_rating_str: 1
            },
            sort: <IReviews_S>{ createdAt: -1 }
        };
        let resp = await this.reviewsService.query(query);
        this.cd.detectChanges();
        this.loading = false;
        return resp;
    }

    isPhoneCase(mainCategoryName: string): boolean {
        return /phone[\s-]*case/i.test(mainCategoryName);
    }

    scrollToCategories(categorySection: HTMLDetailsElement) {
        categorySection.open = true;
        categorySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
